import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card, CardBack } from "../components/Card";
import { gameSeatRender, lobbySeatRenderCompact, paint, SeatRing } from "../components/SeatRing";
import { useWakeLock } from "../wakeLock";
import type { GameStore } from "../gameStore";

export function TdcView({ store }: { store: GameStore }): JSX.Element {
  // The shared table device must stay awake the entire time the app is open.
  useWakeLock(true);

  const phase = store.tdcView?.phase ?? store.lobby?.phase ?? "LOBBY";

  // Lobby start state — needed both for the lobby render and the footer button.
  const lobby = store.lobby;
  const seatedCount = lobby?.seats.filter((s) => s.participantId).length ?? 0;
  const readyCount = lobby?.seats.filter((s) => s.isReady).length ?? 0;
  const canStart = seatedCount >= 2 && readyCount === seatedCount;
  const inLobby = phase === "LOBBY";

  // Local "starting…" state for the Start button. The server takes a noticeable
  // moment to deal cards and broadcast COUNTDOWN; without immediate feedback
  // the button looks dead. Cleared by the lobby→countdown phase transition,
  // or by a 6s safety timeout if the start fails silently.
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    if (!inLobby) setStarting(false);
  }, [inLobby]);
  useEffect(() => {
    if (store.rejectMessage) setStarting(false);
  }, [store.rejectMessage]);

  return (
    <div className="tdc-root">
      <div className="tdc-header">
        <span className="brand">UnoPhone</span>
        {store.lobby?.gameName && <span className="muted">— {store.lobby.gameName}</span>}
        <span className="right muted">
          {store.socketStatus === "connected" ? "● live" : `● ${store.socketStatus}`}
        </span>
      </div>

      {phase === "LOBBY" || phase === "COUNTDOWN" ? (
        <TdcLobby store={store} />
      ) : (
        <TdcGame store={store} />
      )}

      <div className="tdc-footer">
        {phase !== "ENDED" && (
          <button
            className="danger"
            onClick={() => {
              if (window.confirm("End the game?")) store.emit("end_game");
            }}
          >
            End game
          </button>
        )}
        {inLobby && (
          <button
            className={`success tdc-footer-start${starting ? " is-starting" : ""}`}
            disabled={!canStart || starting}
            onClick={() => {
              if (!canStart || starting) return;
              setStarting(true);
              store.emit("start_game");
              setTimeout(() => setStarting(false), 6000);
            }}
          >
            {starting ? (
              <>
                <span className="btn-spinner" aria-hidden />
                <span>Starting…</span>
              </>
            ) : canStart ? (
              "Start game"
            ) : (
              "Waiting for players…"
            )}
          </button>
        )}
        <span className="right muted">
          Code <strong style={{ color: "var(--accent)" }}>{store.welcome?.gameCode ?? "----"}</strong>
        </span>
      </div>

      {store.ended && <EndScreen store={store} />}
    </div>
  );
}

function TdcLobby({ store }: { store: GameStore }): JSX.Element {
  const lobby = store.lobby;
  if (!lobby) return <div className="center"><div className="muted">loading lobby…</div></div>;

  const joinUrl = `${window.location.origin}/?code=${lobby.gameCode}`;
  const SEATS_VISIBLE = 8;
  const seats = lobby.seats.slice(0, SEATS_VISIBLE);

  const seatedCount = lobby.seats.filter((s) => s.participantId).length;
  const readyCount = lobby.seats.filter((s) => s.isReady).length;

  const onSeatTap = (seatIndex: number, participantId: string | null, name: string | null): void => {
    if (lobby.phase !== "LOBBY" || !participantId) return;
    if (window.confirm(`Remove ${name ?? "player"} from seat ${seatIndex + 1}?`)) {
      store.emit("kick_player", { participantId });
    }
  };

  return (
    <div className="tdc-table">
      <SeatRing
        compact
        size={SEATS_VISIBLE}
        seats={seats.map((s) => ({
          seatIndex: s.seatIndex,
          render: lobbySeatRenderCompact(s),
          className: !s.participantId
            ? "empty"
            : s.isReady
              ? "ready"
              : s.isConnected
                ? "taken"
                : "taken disconnected",
          onTap: s.participantId
            ? () => onSeatTap(s.seatIndex, s.participantId, s.name)
            : undefined,
        }))}
      />
      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {lobby.phase === "COUNTDOWN" ? (
          <div style={{ fontSize: "8rem", fontWeight: 800, color: "var(--accent)" }}>
            {store.countdownSeconds ?? 3}
          </div>
        ) : (
          <>
            <div className="qr-block">
              <QRCodeSVG value={joinUrl} size={180} includeMargin />
              <div style={{ color: "var(--fg)", fontSize: "0.95rem", fontWeight: 600 }}>
                Scan to join
              </div>
            </div>
            <div className="code-display">{lobby.gameCode}</div>
          </>
        )}
      </div>

      {lobby.phase !== "COUNTDOWN" && (
        <div className="lobby-status-strip">
          {seatedCount}/{lobby.maxSeats} players · {readyCount} ready · tap a seat to remove player from game
        </div>
      )}
    </div>
  );
}

const SEATS_VISIBLE = 8;

// Walk seat order in `direction` from `currentIndex` and return the next *seated*
// player. Skip-card semantics aren't applied — the strip shows simple turn order
// (the live announce/glow corrects the picture once a skip resolves).
function nextSeatedPlayer(
  seats: { seatIndex: number; name: string; color: string }[],
  currentIndex: number,
  direction: 1 | -1,
): { seatIndex: number; name: string; color: string } | null {
  if (seats.length === 0) return null;
  const sorted = [...seats].sort((a, b) => a.seatIndex - b.seatIndex);
  const orderedAsc = direction === 1 ? sorted : sorted.slice().reverse();
  const currentPos = orderedAsc.findIndex((s) => s.seatIndex === currentIndex);
  if (currentPos === -1) return orderedAsc[0] ?? null;
  return orderedAsc[(currentPos + 1) % orderedAsc.length] ?? null;
}

// Geometric source-position for the fly-in animation. Mirrors SeatRing's layout
// (radius = 0.4 of the seat-ring container, container = min(78vmin, 700px)).
function seatOffsetPx(seatIndex: number, totalSeats: number): { x: number; y: number } {
  const ringSize = Math.min(0.78 * Math.min(window.innerWidth, window.innerHeight), 700);
  const radius = 0.4 * ringSize;
  const angle = (seatIndex / totalSeats) * 2 * Math.PI - Math.PI / 2;
  return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
}

function TdcGame({ store }: { store: GameStore }): JSX.Element {
  const view = store.tdcView;
  const [announceSeat, setAnnounceSeat] = useState<number | null>(null);
  const [colorFlash, setColorFlash] = useState<string | null>(null);
  const [arrowKey, setArrowKey] = useState(0);
  const [drawAnnounce, setDrawAnnounce] = useState<{ seatIndex: number; count: number; key: number } | null>(null);
  const lastSeatRef = useRef<number | null>(null);
  const lastPhaseRef = useRef<string | null>(null);
  const lastAwaitingRef = useRef<number | null>(null);
  const lastDirectionRef = useRef<number | null>(null);
  const lastDrawnAtRef = useRef<number | null>(null);
  const drawKeyRef = useRef(0);

  // Surface a "{player} drew" banner + fly-to-seat card-back whenever a
  // card_drawn event arrives. Without this it's hard to spot at the table
  // when a player drew rather than playing — the discard pile doesn't change.
  useEffect(() => {
    const ld = store.lastDrawn;
    if (!ld) return;
    if (lastDrawnAtRef.current === ld.at) return;
    lastDrawnAtRef.current = ld.at;
    drawKeyRef.current += 1;
    setDrawAnnounce({ seatIndex: ld.seatIndex, count: ld.count, key: drawKeyRef.current });
    const t = setTimeout(() => {
      setDrawAnnounce((d) => (d && d.key === drawKeyRef.current ? null : d));
    }, 1600);
    return () => clearTimeout(t);
  }, [store.lastDrawn]);

  // Turn-change announcement on currentSeatIndex transitions during active play.
  useEffect(() => {
    if (!view) return;
    const playing =
      view.phase === "PLAYING" ||
      view.phase === "AWAITING_COLOR" ||
      view.phase === "CHALLENGE_WINDOW";
    const justEntered = lastPhaseRef.current !== view.phase && playing;
    lastPhaseRef.current = view.phase;
    if (!playing) {
      lastSeatRef.current = null;
      return;
    }
    if (lastSeatRef.current === view.currentSeatIndex && !justEntered) return;
    lastSeatRef.current = view.currentSeatIndex;
    setAnnounceSeat(view.currentSeatIndex);
    const t = setTimeout(() => setAnnounceSeat(null), 1400);
    return () => clearTimeout(t);
  }, [view?.currentSeatIndex, view?.phase]);

  // Color flash when awaitingColorBy clears on a wild top (i.e. someone just
  // resolved a wild_color_choose). Skipped on initial mount.
  useEffect(() => {
    const wasAwaiting = lastAwaitingRef.current !== null;
    const nowAwaiting = view?.awaitingColorBy ?? null;
    lastAwaitingRef.current = nowAwaiting;
    if (
      wasAwaiting &&
      nowAwaiting === null &&
      view?.currentColor &&
      view?.topOfDiscard?.color === "wild"
    ) {
      setColorFlash(view.currentColor);
      const t = setTimeout(() => setColorFlash(null), 750);
      return () => clearTimeout(t);
    }
  }, [view?.awaitingColorBy, view?.currentColor, view?.topOfDiscard?.color]);

  // Direction-arrow spin when reverse flips the play direction.
  useEffect(() => {
    if (!view) return;
    if (lastDirectionRef.current !== null && lastDirectionRef.current !== view.direction) {
      setArrowKey((k) => k + 1);
    }
    lastDirectionRef.current = view.direction;
  }, [view?.direction]);

  if (!view) return <div className="center"><div className="muted">dealing…</div></div>;

  const seats = view.seats;
  const seatBySeat = new Map(seats.map((s) => [s.seatIndex, s]));
  const announcedPlayer = announceSeat !== null ? seatBySeat.get(announceSeat) : null;
  const currentPlayer = seatBySeat.get(view.currentSeatIndex) ?? null;
  const nextPlayer = nextSeatedPlayer(seats, view.currentSeatIndex, view.direction);
  const showUpNextStrip =
    view.phase === "PLAYING" ||
    view.phase === "AWAITING_COLOR" ||
    view.phase === "CHALLENGE_WINDOW";

  // Compute fly-from-seat origin if the current top matches the most recent
  // card_played event. Otherwise the discard-slot keyframe falls back to its
  // default drop-from-above (e.g. initial deal).
  const lastPlayed = store.lastPlayed;
  const topMatchesLastPlay =
    lastPlayed && view.topOfDiscard && view.topOfDiscard.id === lastPlayed.cardId;
  const fromOffset = topMatchesLastPlay
    ? seatOffsetPx(lastPlayed!.seatIndex, SEATS_VISIBLE)
    : null;
  const discardSlotStyle = fromOffset
    ? ({
        ["--from-x" as string]: `${fromOffset.x}px`,
        ["--from-y" as string]: `${fromOffset.y}px`,
        ["--from-rot" as string]: `${Math.max(-22, Math.min(22, fromOffset.x / 14))}deg`,
      } as React.CSSProperties)
    : undefined;

  return (
    <div className="tdc-table">
      <SeatRing
        size={SEATS_VISIBLE}
        seats={Array.from({ length: SEATS_VISIBLE }, (_, i) => {
          const seat = seatBySeat.get(i);
          if (!seat) {
            return {
              seatIndex: i,
              render: <div className="muted">—</div>,
              className: "empty",
            };
          }
          const isActive = view.currentSeatIndex === i;
          return {
            seatIndex: i,
            render: gameSeatRender(seat, isActive),
            className: `taken ${isActive ? "active" : ""} ${seat.isConnected ? "" : "disconnected"}`,
          };
        })}
      />

      <div className="tdc-center" style={{ position: "absolute" }}>
        <div className="pile">
          <div className="pile-label">Deck ({view.deckSize})</div>
          <div
            className="deck-stack"
            onClick={() => store.emit("draw_card")}
            title="Tap to draw for current player"
          >
            <CardBack />
            <CardBack />
          </div>
        </div>
        <div className="pile">
          <div className="pile-label">
            Discard ({view.discardSize}) {view.currentColor ? `· ${view.currentColor}` : ""}
            {" "}
            <span className="direction-arrow" key={`dir-${arrowKey}`}>
              {view.direction === -1 ? "↺" : "→"}
            </span>
          </div>
          {view.topOfDiscard ? (
            <div className="discard-stack">
              {/* Show up to 2 layers of "depth" beneath the visible top card so
                  the pile illustrates how big the discard has grown. */}
              {view.discardSize >= 3 && <div className="discard-shadow shadow-2" aria-hidden />}
              {view.discardSize >= 2 && <div className="discard-shadow shadow-1" aria-hidden />}
              <div
                className="discard-slot"
                key={view.topOfDiscard.id}
                style={discardSlotStyle}
              >
                <Card card={view.topOfDiscard} />
              </div>
            </div>
          ) : (
            <div className="card-back" />
          )}
        </div>
      </div>

      {announcedPlayer && (
        <div className="turn-announce" key={`announce-${announceSeat}`}>
          <span className="turn-badge" style={{ background: paint(announcedPlayer.color) }} aria-hidden />
          <span>{announcedPlayer.name}'s turn</span>
        </div>
      )}

      {drawAnnounce && (() => {
        const seat = seatBySeat.get(drawAnnounce.seatIndex);
        if (!seat) return null;
        const off = seatOffsetPx(drawAnnounce.seatIndex, SEATS_VISIBLE);
        const flyStyle = {
          ["--to-x" as string]: `${off.x}px`,
          ["--to-y" as string]: `${off.y}px`,
          ["--to-rot" as string]: `${Math.max(-22, Math.min(22, off.x / 14))}deg`,
        } as React.CSSProperties;
        return (
          <>
            <div className="draw-announce" key={`draw-banner-${drawAnnounce.key}`}>
              <span className="turn-badge" style={{ background: paint(seat.color) }} aria-hidden />
              <span>
                {seat.name} drew{drawAnnounce.count > 1 ? ` ${drawAnnounce.count}` : ""}
              </span>
            </div>
            <div
              className="draw-fly"
              key={`draw-fly-${drawAnnounce.key}`}
              style={flyStyle}
              aria-hidden
            >
              <CardBack />
            </div>
          </>
        );
      })()}

      {showUpNextStrip && currentPlayer && (
        <div className="up-next-strip" key={`upnext-${view.currentSeatIndex}-${nextPlayer?.seatIndex ?? "x"}`}>
          <div className="up-next-now">
            <span className="up-next-label">Now</span>
            <span className="turn-badge" style={{ background: paint(currentPlayer.color) }} aria-hidden />
            <span className="up-next-name">{currentPlayer.name}</span>
          </div>
          <span className="up-next-arrow" aria-hidden>{view.direction === -1 ? "↺" : "→"}</span>
          <div className="up-next-next">
            <span className="up-next-label">Up next</span>
            <span className="turn-badge" style={{ background: paint(nextPlayer?.color ?? "gray") }} aria-hidden />
            <span className="up-next-name">{nextPlayer?.name ?? "—"}</span>
          </div>
        </div>
      )}

      {colorFlash && <div className={`color-flash flash-${colorFlash}`} key={`flash-${colorFlash}`} />}

      {view.phase === "ROUND_END" && view.roundWinner && (
        <div style={{ position: "absolute", top: "10%" }} className="winner-banner">
          🎉 {view.roundWinner.name} wins round {view.roundNumber}!
        </div>
      )}
      {view.phase === "REPLAY_PROMPT" && (() => {
        const seatedVotes = view.replayVotes;
        const cast = seatedVotes.filter((v) => v.vote !== null).length;
        const yes = seatedVotes.filter((v) => v.vote === true).length;
        const no = seatedVotes.filter((v) => v.vote === false).length;
        const allVoted = cast === seatedVotes.length && seatedVotes.length > 0;
        const split = allVoted && yes > 0 && no > 0;
        return (
          <div className="replay-panel">
            <div className="replay-panel-title">Play again?</div>
            <div className="replay-panel-tally">
              <span className="tally-yes">✓ {yes}</span>
              <span className="tally-no">✗ {no}</span>
              <span className="muted">· {cast}/{seatedVotes.length} voted</span>
            </div>
            <div className="replay-panel-seats">
              {seatedVotes.map((v) => {
                const seat = seatBySeat.get(v.seatIndex);
                if (!seat) return null;
                const state = v.vote === true ? "yes" : v.vote === false ? "no" : "pending";
                return (
                  <div key={v.seatIndex} className={`replay-seat replay-seat-${state}`}>
                    <span className="badge" style={{ background: paint(seat.color) }} aria-hidden />
                    <span className="replay-seat-name">{seat.name}</span>
                    <span className="replay-seat-vote">
                      {v.vote === true ? "✓" : v.vote === false ? "✗" : "…"}
                    </span>
                  </div>
                );
              })}
            </div>
            {split && (
              <div className="replay-panel-actions">
                <div className="muted" style={{ marginBottom: "0.5rem" }}>
                  Players are split — table device decides:
                </div>
                <div className="row">
                  <button
                    className="success"
                    onClick={() => store.emit("tdc_resolve_replay", { resume: true })}
                  >
                    Play again
                  </button>
                  <button
                    className="danger"
                    onClick={() => store.emit("tdc_resolve_replay", { resume: false })}
                  >
                    End game
                  </button>
                </div>
              </div>
            )}
            {!split && allVoted && (
              <div className="muted">Resolving…</div>
            )}
            {!allVoted && (
              <div className="muted">Waiting for everyone to vote…</div>
            )}
          </div>
        );
      })()}
      {view.unoChallenge && (
        <div style={{ position: "absolute", bottom: "20%" }} className="winner-banner">
          UNO challenge — call out seat {view.unoChallenge.challengedSeatIndex + 1}!
        </div>
      )}
    </div>
  );
}

function EndScreen({ store }: { store: GameStore }): JSX.Element {
  const ended = store.ended!;
  return (
    <div className="modal-backdrop">
      <div className="modal col">
        <div className="brand">Game over</div>
        <div className="muted">{ended.reason.replace(/_/g, " ")}</div>
        <div className="history-list">
          {ended.history.map((h) => (
            <div className="item" key={h.roundNumber}>
              <strong>Round {h.roundNumber}</strong>
              <span style={{ color: "var(--accent)" }}>{h.winnerName}</span>
            </div>
          ))}
        </div>
        <button onClick={() => window.location.reload()}>New game</button>
      </div>
    </div>
  );
}
